import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowUpRight, Check, Instagram } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/wyeeJtwvu1HiXsJKfGXny6/1.png';
const ICE_IMAGE = 'https://cdn.magicpatterns.com/uploads/knKE32EsZkYsqPa1ui7YrB/DSC03885.jpg';
const SOCIAL_IMAGE = 'https://cdn.magicpatterns.com/uploads/m4VHGC1GgxgtGcoFZGpH81/IMG_0693.jpg';
const CAN_IMAGE = 'https://cdn.magicpatterns.com/uploads/wDu3fKhK1RBeGFe1tY72ry/DSC03888.jpg';
const VIDEO_MOMENT = 'https://cdn.magicpatterns.com/uploads/macVvEoEwdpGBSYDC8PDiv/CPSTHLM_30.mp4';
const PEOPLE_IMAGE_1 = 'https://cdn.magicpatterns.com/uploads/1KzvTsy3KfZ88WPDkaVLMn/WAGHORN_2026-03-22_18.jpg';
const PEOPLE_IMAGE_2 = 'https://cdn.magicpatterns.com/uploads/oLZUyGYJTdHH83PscaSwsS/IMG_0700.jpg';
const ICE_BOWL_IMAGE = 'https://cdn.magicpatterns.com/uploads/p4Sk3dNMD8P9BQwDabDdK8/DSC04025.jpg';
const INSTAGRAM_URL = 'https://www.instagram.com/beatyourbest_?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==';
const CONTACT_EMAIL = 'info@beatsoda.com';
const WEB3FORMS_KEY = 'a23b9419-6869-4092-bd1e-0a48a2461c5e';
const EASE = [0.23, 1, 0.32, 1] as const;
const rise = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: EASE
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
};
const WORLD_WORDS = ['Music', 'Food', 'Friends', 'Travel', 'Sport', 'Art', 'Nightlife', 'Design', 'Adventure'];
const FLAVOURS = [{
  name: 'Red',
  mood: 'Glow',
  line: 'Deep beetroot and grapefruit. Earthy, bright, a little bittersweet.',
  moment: 'Slow moments.',
  color: '#B0203A',
  tint: '#F6E9EC'
}, {
  name: 'Yellow',
  mood: 'Flow',
  line: 'Golden beet, lemon and elderflower. Softer, rounder, citrus-led.',
  moment: 'When everything clicks.',
  color: '#D9A21B',
  tint: '#FAF3E2'
}, {
  name: 'White',
  mood: 'Zing',
  line: 'White beet, pomegranate and cider vinegar. Crisp, dry, sharp finish.',
  moment: 'When you want more.',
  color: '#B9B2A6',
  tint: '#F4F2EE'
}];
const INGREDIENTS = ['Beetroot', 'Grapefruit', 'Pomegranate', 'Lemon', 'Elderflower', 'Apple Cider Vinegar'];
export function LandingPage() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="w-full bg-beat-black font-sans text-white antialiased" data-id="element-1" data-mp="2zrwrgu3wjvae">
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-screen w-full overflow-hidden" data-id="element-2" data-mp="2vsfoq8fv1c8x">
        <motion.img src={ICE_IMAGE} alt="A cold BEAT can buried in crushed ice" initial={{
        opacity: 0,
        scale: 1.06
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.4,
        ease: EASE
      }} className="absolute inset-0 h-full w-full object-cover object-center" data-id="element-3" data-mp="y01xbsdow9fx" />
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to top, rgba(10,10,10,0.88) 0%, rgba(10,10,10,0.45) 45%, rgba(10,10,10,0.12) 75%, rgba(255,255,255,0.35) 100%)'
      }} data-id="element-4" data-mp="2lp0si7xgppyg" />

        <BeatMark className="absolute left-5 top-8 z-20 w-28 md:left-10 md:top-10 md:w-32" data-id="element-5" data-mp="1xy4uuu6bs0ja" />

        <motion.div initial="hidden" animate="visible" variants={stagger} className="relative z-10 flex min-h-screen flex-col justify-end px-5 pb-20 pt-40 md:px-10 md:pb-24" data-id="element-6" data-mp="21xvrvfmg3cw9">
          <motion.p variants={rise} className="mb-6 text-sm font-medium tracking-widest text-white/70 uppercase" data-id="element-7" data-mp="3ddu7k2c4ox6g">
            Good taste · Good people · Good times
          </motion.p>

          <motion.h1 variants={rise} className="max-w-[18ch] text-5xl font-medium leading-[1.1] tracking-tight md:text-7xl lg:text-8xl" data-id="element-8" data-mp="3gxuocu1bz41j">
            A better way to experience <span className="text-beat-red" data-id="element-9" data-mp="3pud4t6sdkzh8">life.</span>
          </motion.h1>

          <motion.div variants={rise} className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-center" data-id="element-10" data-mp="3ltt5pxo8rlsg">
            <button onClick={() => scrollTo('product')} className="group inline-flex items-center gap-3 rounded-full bg-beat-red px-8 py-4 text-base font-medium text-white transition-colors duration-150 hover:bg-white hover:text-beat-black" style={{
            transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)'
          }} data-id="element-11" data-mp="3uw2eoj3v373q">
              Discover BEAT
              <ArrowUpRight size={18} className="transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-12" data-mp="hhxq9z2spv73" />
            </button>
            <p className="max-w-xs text-base text-white/60" data-id="element-13" data-mp="160g9c496p24r">
              A Real Root & Fruit Soda 
            </p>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.6,
        duration: 0.6
      }} className="absolute bottom-8 right-5 z-10 text-white/50 md:right-10" data-id="element-14" data-mp="w3bj9tc7wmio">
          <ArrowDown size={22} className="animate-bounce" data-id="element-15" data-mp="8eiuqhxxdgy3" />
        </motion.div>
      </section>

      {/* ── 02: THIS IS BEAT (PRODUCT) ───────────────────────── */}
      <section id="product" className="border-b border-white/10 px-5 py-24 md:px-10 md:py-36" data-id="element-16" data-mp="dar4tgx9mjye">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-2 lg:gap-20" data-id="element-17" data-mp="4c5q5wuwzp2w">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} data-id="element-18" data-mp="261bcryz9zirj">
            <motion.p variants={rise} className="mb-8 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-19" data-mp="1wctf3qmalrs4">
              The Drink
            </motion.p>
            <motion.h2 variants={rise} className="text-4xl font-medium tracking-tight md:text-6xl" data-id="element-20" data-mp="2oze6us6ok5w7">
              This is BEAT.
            </motion.h2>
            <motion.p variants={rise} className="mt-8 max-w-md text-xl leading-relaxed text-white/70" data-id="element-21" data-mp="2fypr9t1n1xh7">
              A drink for the moments worth remembering. Bright, bold, rewarding, and balanced.
            </motion.p>

            <motion.div variants={rise} className="mt-12 flex items-center gap-4" data-id="element-22" data-mp="zliu77ymsd6t">
              <div className="h-4 w-4 rounded-full bg-beat-red" data-id="element-23" data-mp="znvjy7amrruf" />
              <span className="text-base font-medium" data-id="element-24" data-mp="3ba5p7z9moyv4">Red</span>
              <div className="h-4 w-4 rounded-full bg-yellow-400" data-id="element-25" data-mp="3g7fp68un8sx7" />
              <span className="text-base font-medium" data-id="element-26" data-mp="3g5l3utfgmysl">Yellow</span>
              <div className="h-4 w-4 rounded-full bg-white" data-id="element-27" data-mp="3k8r9byur0ly0" />
              <span className="text-base font-medium" data-id="element-28" data-mp="3p0srg1ell6vy">White</span>
            </motion.div>

            <motion.button variants={rise} onClick={() => scrollTo('flavours')} className="mt-12 inline-flex items-center gap-2 text-base font-medium text-beat-red transition-colors hover:text-white" data-id="element-29" data-mp="3tzm45rm28tv2">
              Discover the flavours <ArrowDown size={18} data-id="element-30" data-mp="7qb6tzqfgjro" />
            </motion.button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 28
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.8,
          ease: EASE
        }} className="relative aspect-[4/5] w-full overflow-hidden rounded-3xl" data-id="element-31" data-mp="brmxm8l1fb1g">
            <img src={CAN_IMAGE} alt="A BEAT can resting on a railing" className="h-full w-full object-cover" style={{
            paddingTop: "274px"
          }} data-id="element-32" data-mp="23gnq6l3iiytg" />
          </motion.div>
        </div>
      </section>

      {/* ── 02B: THE FLAVOURS ────────────────────────────────── */}
      <section id="flavours" className="rounded-t-[3rem] bg-[#F5F3EF] px-5 py-24 text-beat-black md:px-10 md:py-36" data-id="element-33" data-mp="16rof6ybjnixk">
        <div className="mx-auto max-w-[1400px]" data-id="element-34" data-mp="1krz784olb3kb">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid gap-10 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-end" data-id="element-35" data-mp="1fr33br48nhew">
            <div data-id="element-36" data-mp="2zmpayh0ru16o">
              <motion.p variants={rise} className="mb-6 text-sm font-medium uppercase tracking-widest text-beat-red" data-id="element-37" data-mp="2zm7af872t0sy">
                The Flavours
              </motion.p>
              <motion.h2 variants={rise} className="text-4xl font-medium leading-[1.05] tracking-tight md:text-6xl" data-id="element-38" data-mp="2prp4rxi3mv6i">
                Meet BEAT.
              </motion.h2>
              <motion.div variants={rise} className="mt-8 h-px w-16 bg-beat-red" data-id="element-39" data-mp="3ndcy29j9cxdt" />
            </div>
            <motion.p variants={rise} className="max-w-md text-xl leading-relaxed text-black/60" data-id="element-40" data-mp="38nnj9bi8suv2">
              Bold. Refreshing. Made from real ingredients — three beets, three
              moods, for real moments.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} className="mt-16 grid gap-6 md:grid-cols-3 md:gap-8" data-id="element-41" data-mp="9z883l2jndf2">
            {FLAVOURS.map(flavour => <motion.article key={flavour.name} variants={rise} className="overflow-hidden rounded-3xl bg-white" data-id="element-42" data-mp="24569pg7ruwx1">
                <div className="flex aspect-[4/3] items-center justify-center" style={{
              backgroundColor: flavour.tint
            }} data-id="element-43" data-mp="1ubpfwc7mxhtd">
                  <div className="h-28 w-28 rounded-full md:h-32 md:w-32" style={{
                backgroundColor: flavour.color
              }} data-id="element-44" data-mp="1la96o7sk3lqq" />
                </div>
                <div className="p-8" data-id="element-45" data-mp="1gdsk3i109ffr">
                  <div className="flex items-baseline justify-between gap-4" data-id="element-46" data-mp="1gcpo4votsmsz">
                    <h3 className="text-2xl font-medium tracking-tight md:text-3xl" data-id="element-47" data-mp="wsil5izbsmlz">
                      {flavour.name}
                    </h3>
                    <span className="text-sm font-medium uppercase tracking-widest text-beat-red" data-id="element-48" data-mp="ruqs2lmy2ug7">
                      {flavour.mood}
                    </span>
                  </div>
                  <p className="mt-4 text-base leading-relaxed text-black/60" data-id="element-49" data-mp="11sn034kl59a3">
                    {flavour.line}
                  </p>
                  <p className="mt-6 border-t border-black/10 pt-6 text-base font-medium text-black/80" data-id="element-50" data-mp="1arry89s8j0lq">
                    {flavour.moment}
                  </p>
                </div>
              </motion.article>)}
          </motion.div>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={rise} className="mt-16 text-base text-black/50" data-id="element-51" data-mp="1khtlx0goeh04">
            No alcohol. No caffeine. Nothing artificial.
          </motion.p>
        </div>
      </section>

      {/* ── MARQUEE ──────────────────────────────────────────── */}
      <Marquee data-id="element-52" data-mp="26f629al4hv0v" />

      {/* ── 03: THE WORLD ────────────────────────────────────── */}
      <section id="world" className="border-b border-white/10 px-5 py-24 md:px-10 md:py-36" data-id="element-53" data-mp="2beinjqwciu1c">
        <div className="mx-auto max-w-[1400px]" data-id="element-54" data-mp="2g8mxmj0wri6n">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-20" data-id="element-55" data-mp="2k9f9lk16016m">
            <div data-id="element-56" data-mp="2z54xxottoqei">
              <motion.p variants={rise} className="mb-8 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-57" data-mp="2z5o4k9e0vsd8">
                The World
              </motion.p>
              <motion.p variants={rise} className="text-3xl font-medium leading-snug text-white md:text-4xl" data-id="element-58" data-mp="14u9d9o5d5kvd">
                BEAT lives where good people, good taste and good experiences
                meet.
              </motion.p>
              <motion.p variants={rise} className="mt-8 max-w-sm text-lg leading-relaxed text-white/60" data-id="element-59" data-mp="39amlccp40bva">
                A dinner. A festival. A studio. A beach. A basement bar at 1am.
                The place changes. The feeling doesn't.
              </motion.p>
            </div>

            <motion.div variants={rise} data-id="element-60" data-mp="sgt42fm5m9m">
              <ul className="flex flex-wrap items-baseline gap-x-6 gap-y-3" data-id="element-61" data-mp="3rzjicobw5ih7">
                {WORLD_WORDS.map(word => <li key={word} className="text-4xl font-medium tracking-tight text-white/85 md:text-6xl lg:text-7xl" data-id="element-62" data-mp="3n0zsicu6fmjk">
                    {word}
                    <span className="text-beat-red" data-id="element-63" data-mp="2bl1hi3ffuqfp">.</span>
                  </li>)}
              </ul>
              <p className="mt-16 text-2xl font-medium text-beat-red md:text-4xl" data-id="element-64" data-mp="3btmvr9ciowy">
                Something is happening.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 04: PEOPLE WITH A BEAT ───────────────────────────── */}
      <section className="bg-white px-5 py-24 text-beat-black md:px-10 md:py-36 rounded-t-[3rem]" data-id="element-65" data-mp="3vfcnrs9aayd7">
        <div className="mx-auto max-w-[1400px]" data-id="element-66" data-mp="mzj79deydmw5">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid gap-16 lg:grid-cols-2 lg:gap-24" data-id="element-67" data-mp="r3ijvkx441k8">
            <div data-id="element-68" data-mp="2xtykv4x315c4">
              <motion.p variants={rise} className="mb-8 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-69" data-mp="2xtf802mah74y">
                People with a beat
              </motion.p>
              <motion.h2 variants={rise} className="text-4xl font-medium tracking-tight md:text-6xl" data-id="element-70" data-mp="2v7pfzi5s21c2">
                BEAT belongs to people with their own taste.
              </motion.h2>

              <motion.div variants={stagger} className="mt-16 grid gap-10 border-t border-black/10 pt-12" data-id="element-71" data-mp="2zaug5e2dxqxj">
                <PeopleItem title="Create" copy="Ideas that land. Nights that stay sharp all the way to the end." data-id="element-72" data-mp="2bm2ruxrx1sq1" />
                <PeopleItem title="Connect" copy="Conversations you actually remember. Presence you can feel." data-id="element-73" data-mp="2lbmb44jjjolf" />
                <PeopleItem title="Move" copy="A body that shows up the next morning, ready to go again." data-id="element-74" data-mp="2gijgdt31293n" />
              </motion.div>
            </div>

            <motion.div variants={rise} className="grid grid-cols-2 gap-4 md:gap-6" data-id="element-75" data-mp="1ntumae0e341e">
              <div className="mt-12 md:mt-24" data-id="element-76" data-mp="3swhvcu6y7668">
                <img src={PEOPLE_IMAGE_1} alt="Candid shot of a woman drinking BEAT at a party" className="aspect-[3/4] w-full object-cover rounded-3xl" data-id="element-77" data-mp="3sv6x19w9uqmi" />
              </div>
              <div data-id="element-78" data-mp="34zm0n95cumit">
                <img src={PEOPLE_IMAGE_2} alt="Candid shot of a man drinking BEAT with sunglasses" className="aspect-[3/4] w-full object-cover rounded-3xl" data-id="element-79" data-mp="35046mxhtkf3x" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 05: TASTE ────────────────────────────────────────── */}
      <section className="border-b border-white/10 px-5 py-24 md:px-10 md:py-36" data-id="element-80" data-mp="u7suzie13lll">
        <div className="mx-auto max-w-[1400px]" data-id="element-81" data-mp="y83d1tf3i2gt">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="text-center" data-id="element-82" data-mp="y6tbgu91ac5f">
            <motion.p variants={rise} className="mb-8 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-83" data-mp="29xk24ighiokl">
              Taste
            </motion.p>
            <motion.h2 variants={rise} className="mx-auto max-w-[18ch] text-4xl font-medium tracking-tight md:text-6xl" data-id="element-84" data-mp="200fmz2l23tda">
              Flavor. Texture. Pairing. Moment.
            </motion.h2>
            
            <motion.ul variants={rise} className="mt-16 flex flex-wrap justify-center gap-x-6 gap-y-4 text-base font-medium text-white/70 md:text-lg" data-id="element-85" data-mp="1r1lkivl1o8rt">
              {INGREDIENTS.map((item, index) => <li key={item} className="flex items-center gap-6" data-id="element-86" data-mp="3gdvzihbhg2f4" data-mpi={index}>
                  {index > 0 && <span className="text-beat-red" data-id="element-87" data-mp="2p9blpegqcf3l">·</span>}
                  {item}
                </li>)}
            </motion.ul>
          </motion.div>
        </div>
      </section>

      {/* ── 06: THE WORLD IN MOTION ──────────────────────────── */}
      <Moment src={VIDEO_MOMENT} alt="Bartender grabbing a BEAT can from an ice bowl" label="Club · 23:45" isVideo data-id="element-88" data-mp="2fj06gvrlijhe" />

      {/* ── 07: THE STORY ────────────────────────────────────── */}
      <section className="px-5 py-24 md:px-10 md:py-36" data-id="element-89" data-mp="2ak73hx9xqavh">
        <div className="mx-auto max-w-[1400px]" data-id="element-90" data-mp="2alqjdmroijwq">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="grid gap-14 lg:grid-cols-2 lg:gap-20" data-id="element-91" data-mp="1wknvr5l1d1jp">
            <div data-id="element-92" data-mp="3vxi6jeg857hu">
              <motion.p variants={rise} className="mb-8 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-93" data-mp="3vy1pmyhm21xg">
                The Story
              </motion.p>
              <motion.h2 variants={rise} className="text-4xl font-medium tracking-tight md:text-6xl" data-id="element-94" data-mp="1fbv3w7sqbb0b">
                São Paulo &rarr; Stockholm &rarr; BEAT.
              </motion.h2>
            </div>
            <div className="flex flex-col justify-center" data-id="element-95" data-mp="1p76i8p086g7j">
              <motion.p variants={rise} className="text-xl leading-relaxed text-white/70 md:text-2xl" data-id="element-96" data-mp="22zmlnqx1gg8z">
                Someone tasted something amazing. They couldn't stop thinking about it. They brought the idea home. They met the right people. BEAT happened.
              </motion.p>
              <motion.p variants={rise} className="mt-6 text-xl leading-relaxed text-white/70 md:text-2xl" data-id="element-97" data-mp="27xo6aga44nmk">
                The moment matters more than the beet.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── MOMENT: ICE BOWL ─────────────────────────────────── */}
      <Moment src={ICE_BOWL_IMAGE} alt="A large metal bowl filled with ice and BEAT cans at an event" label="Backstage · 21:00" data-id="element-98" data-mp="21r70jrfyyfmv" />

      {/* ── 08: JOIN ─────────────────────────────────────────── */}
      <section className="px-5 py-24 md:px-10 md:py-36" data-id="element-99" data-mp="2bjusa7s3dteq">
        <div className="mx-auto max-w-[1400px]" data-id="element-100" data-mp="1nt8lskxqkh6u">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} variants={stagger} className="mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-101" data-mp="1nu0gltjieaw2">
            <div data-id="element-102" data-mp="395a5kj1u0a6m">
              <motion.p variants={rise} className="mb-6 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-103" data-mp="394qspgr1gbzg">
                Join
              </motion.p>
              <motion.h2 variants={rise} className="text-4xl font-medium tracking-tight md:text-6xl" data-id="element-104" data-mp="3bp6qjn3titqz">
                Join the BEAT.
              </motion.h2>
            </div>
            <motion.a variants={rise} href={`mailto:${CONTACT_EMAIL}`} className="text-2xl font-medium text-white underline decoration-beat-red decoration-2 underline-offset-8 transition-colors duration-150 hover:text-beat-red md:text-4xl" data-id="element-105" data-mp="32r332l99vmwj">
              {CONTACT_EMAIL}
            </motion.a>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} className="grid gap-6 lg:grid-cols-3" data-id="element-106" data-mp="3ln32gh4kxsq5">
            <motion.a variants={rise} href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="group flex flex-col justify-between rounded-3xl bg-white/5 p-8 transition-colors duration-150 hover:bg-beat-red" data-id="element-107" data-mp="m163f3sgx5i6">
              <Instagram size={28} className="mb-16 text-beat-red transition-colors duration-150 group-hover:text-white" data-id="element-108" data-mp="mv5c8nw8xpl4" />
              <div data-id="element-109" data-mp="hw2abcbkbzhd">
                <h3 className="text-2xl font-medium" data-id="element-110" data-mp="hyy5isw10a24">
                  Follow
                </h3>
                <p className="mt-2 flex items-center gap-2 text-base text-white/60 transition-colors duration-150 group-hover:text-white/90" data-id="element-111" data-mp="czvb02do9m8e">
                  @beatyourbest_
                  <ArrowUpRight size={18} data-id="element-112" data-mp="468fmdddmkzq" />
                </p>
              </div>
            </motion.a>

            <NotifyForm data-id="element-113" data-mp="3vdbbhi45tfga" />
            <StockForm data-id="element-114" data-mp="3v9x601078q4m" />
          </motion.div>
        </div>
      </section>

      {/* ── 09: CLOSING ──────────────────────────────────────── */}
      <section className="bg-beat-red px-5 py-28 md:px-10 md:py-40" data-id="element-115" data-mp="3ldaptpitin8x">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={stagger} className="mx-auto max-w-[1400px] flex flex-col items-center text-center" data-id="element-116" data-mp="3b9nfxuwh3rm7">
          <motion.h2 variants={rise} className="text-5xl font-medium tracking-tight md:text-7xl lg:text-8xl" data-id="element-117" data-mp="3l8l2wte93d33">
            Don't waste
            <br data-id="element-118" data-mp="2sk75ofuzkksl" />
            a heartbeat.
          </motion.h2>
          <motion.div variants={rise} className="mt-16" data-id="element-119" data-mp="2xi8px445eq5n">
            <BeatMark className="w-32 md:w-48" data-id="element-120" data-mp="2xiqpa9m3xkhs" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <footer className="px-5 py-14 md:px-10" data-id="element-121" data-mp="3793kcvrjsw26">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-10 border-t border-white/10 pt-10 md:flex-row md:items-center md:justify-between" data-id="element-122" data-mp="379v85kewlphw">
          <BeatMark className="w-28" data-id="element-123" data-mp="2eqpmiw67pa33" />
          <div className="flex flex-col gap-4 text-sm text-white/50 md:flex-row md:items-center md:gap-8" data-id="element-124" data-mp="2epmtoipbwxkj">
            <a href={`mailto:${CONTACT_EMAIL}`} className="transition-colors duration-150 hover:text-white" data-id="element-125" data-mp="k96bqhdbqhi7">
              {CONTACT_EMAIL}
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="transition-colors duration-150 hover:text-white" data-id="element-126" data-mp="u35b58m0jn6g">
              Instagram
            </a>
            <p data-id="element-127" data-mp="z16w5z301wkk">© {new Date().getFullYear()} BEAT Soda</p>
          </div>
        </div>
      </footer>
    </div>;
}
function BeatMark({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`relative flex items-center justify-center ${className}`} data-id="element-128" data-mp="180twehjzswc1">
      <img src={LOGO_URL} alt="BEAT Soda" className="w-full h-auto object-contain" data-id="element-129" data-mp="3erb85qlewpv9" />
    </div>;
}
function Marquee() {
  const line = 'Good taste — Good people — Good times — ';
  return <div className="overflow-hidden bg-beat-red py-5" data-id="element-130" data-mp="1pa5va10evquo">
      <motion.div animate={{
      x: ['0%', '-50%']
    }} transition={{
      duration: 22,
      ease: 'linear',
      repeat: Infinity
    }} className="flex w-max whitespace-nowrap" data-id="element-131" data-mp="1pcs381r7s8df">
        {[0, 1].map(group => <span key={group} className="text-lg font-medium uppercase tracking-widest text-white md:text-xl" data-id="element-132" data-mp="1khwbl2pw3n0u">
            {line.repeat(6)}
          </span>)}
      </motion.div>
    </div>;
}
function Moment({
  src,
  alt,
  label,
  isVideo = false
}: {
  src: string;
  alt: string;
  label: string;
  isVideo?: boolean;
}) {
  return <section className="relative h-[80vh] w-full overflow-hidden md:h-screen" data-id="element-133" data-mp="11lmndml9xg34">
      {isVideo ? <motion.video src={src} autoPlay loop muted playsInline initial={{
      opacity: 0,
      scale: 1.04
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-10%'
    }} transition={{
      duration: 1.1,
      ease: EASE
    }} className="h-full w-full object-cover" data-id="element-134" data-mp="2r12vcyngovrf" /> : <motion.img src={src} alt={alt} initial={{
      opacity: 0,
      scale: 1.04
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-10%'
    }} transition={{
      duration: 1.1,
      ease: EASE
    }} className="h-full w-full object-cover" data-id="element-135" data-mp="2gk0ppchrbqb2" />}
      <p className="absolute bottom-6 left-5 text-sm font-medium tracking-widest text-white/80 uppercase md:left-10" data-id="element-136" data-mp="3vzhoqqjm5dlz">
        {label}
      </p>
    </section>;
}
function PeopleItem({
  title,
  copy
}: {
  title: string;
  copy: string;
}) {
  return <motion.div variants={rise} data-id="element-137" data-mp="1drocrdasqf9y">
      <h3 className="text-3xl font-medium md:text-4xl" data-id="element-138" data-mp="1inmzyxke1r8s">
        {title}
        <span className="text-beat-red" data-id="element-139" data-mp="1mq06ho6ylt6m">.</span>
      </h3>
      <p className="mt-4 max-w-xs text-lg leading-relaxed text-black/60" data-id="element-140" data-mp="1mogp1u9khc39">
        {copy}
      </p>
    </motion.div>;
}
async function sendToBeat(payload: Record<string, string>) {
  const response = await fetch('https://api.web3forms.com/submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      access_key: WEB3FORMS_KEY,
      from_name: 'BEAT Website',
      to: CONTACT_EMAIL,
      ...payload
    })
  });
  return response.ok;
}
function NotifyForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email) return;
    setStatus('loading');
    const ok = await sendToBeat({
      subject: 'BEAT — new launch signup',
      email,
      message: `New launch signup.\n\nEmail: ${email}`
    }).catch(() => false);
    setStatus(ok ? 'success' : 'error');
    if (ok) setEmail('');
  };
  return <motion.div variants={rise} className="flex flex-col justify-between rounded-3xl bg-white/5 p-8" data-id="element-141" data-mp="2096gpwds0okt">
      <p className="mb-16 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-142" data-mp="1cl6lok9fmc0f">
        Launch
      </p>
      <div data-id="element-143" data-mp="17mlmmo5x1ydk">
        <h3 className="text-2xl font-medium" data-id="element-144" data-mp="17mve6g6m0dld">
          Be first
        </h3>
        {status === 'success' ? <p className="mt-4 flex items-center gap-2 text-base font-medium text-beat-red" data-id="element-145" data-mp="1le9yqrg60muj">
            <Check size={18} data-id="element-146" data-mp="1gjm8oo5ijkdu" /> You're on the list
          </p> : <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4" data-id="element-147" data-mp="sul5bfbjezff">
            <input type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="Your email" disabled={status === 'loading'} className="w-full border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-148" data-mp="othreyd1vdgu" />
            <button type="submit" disabled={status === 'loading'} className="self-start text-base font-medium text-white transition-colors duration-150 hover:text-beat-red disabled:opacity-50" data-id="element-149" data-mp="9p6l22jpj8si">
              {status === 'loading' ? 'Sending…' : 'Notify me →'}
            </button>
            {status === 'error' && <p className="text-sm text-white/50" data-id="element-150" data-mp="sgfw4g94m5m7">
                Didn't go through — try again.
              </p>}
          </form>}
      </div>
    </motion.div>;
}
function StockForm() {
  const [form, setForm] = useState({
    company: '',
    email: '',
    notes: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.company || !form.email) return;
    setStatus('loading');
    const ok = await sendToBeat({
      subject: 'BEAT — new stockist enquiry',
      email: form.email,
      company: form.company,
      notes: form.notes || 'No notes',
      message: `New stockist enquiry.\n\nCompany: ${form.company}\nEmail: ${form.email}\nNotes: ${form.notes || 'No notes'}`
    }).catch(() => false);
    setStatus(ok ? 'success' : 'error');
    if (ok) setForm({
      company: '',
      email: '',
      notes: ''
    });
  };
  return <motion.div variants={rise} className="flex flex-col justify-between rounded-3xl bg-white/5 p-8" data-id="element-151" data-mp="25upfzb8j7rlf">
      <p className="mb-16 text-sm font-medium tracking-widest text-beat-red uppercase" data-id="element-152" data-mp="1dc22agvv7eud">
        Trade
      </p>
      <div data-id="element-153" data-mp="15tcb85gx86jv">
        <h3 className="text-2xl font-medium" data-id="element-154" data-mp="15s18m8z7p4uc">
          Stock BEAT
        </h3>
        {status === 'success' ? <p className="mt-4 flex items-center gap-2 text-base font-medium text-beat-red" data-id="element-155" data-mp="10uiv05a921g0">
            <Check size={18} data-id="element-156" data-mp="vtldzw0z7dg3" /> We'll be in touch
          </p> : <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4" data-id="element-157" data-mp="qwl7xl44maoz">
            <input type="text" required value={form.company} onChange={event => setForm({
          ...form,
          company: event.target.value
        })} placeholder="Venue or company" disabled={status === 'loading'} className="w-full border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-158" data-mp="mtyejfrt4hpo" />
            <input type="email" required value={form.email} onChange={event => setForm({
          ...form,
          email: event.target.value
        })} placeholder="Your email" disabled={status === 'loading'} className="w-full border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-159" data-mp="3vateonh7zo02" />
            <textarea rows={2} value={form.notes} onChange={event => setForm({
          ...form,
          notes: event.target.value
        })} placeholder="Volumes, timing, anything else" disabled={status === 'loading'} className="w-full resize-none border-b border-white/25 bg-transparent pb-3 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-160" data-mp="c9cuser9bvad" />
            <button type="submit" disabled={status === 'loading'} className="self-start text-base font-medium text-white transition-colors duration-150 hover:text-beat-red disabled:opacity-50" data-id="element-161" data-mp="159jel0w8ciup">
              {status === 'loading' ? 'Sending…' : 'Send enquiry →'}
            </button>
            {status === 'error' && <p className="text-sm text-white/50" data-id="element-162" data-mp="mdbbo3qowk2k">
                Didn't go through — try again.
              </p>}
          </form>}
      </div>
    </motion.div>;
}